import { Injectable, OnDestroy } from '@angular/core';
import { fromEvent, interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Native Service Worker Update Service
 * 
 * Replaces Angular's SwUpdate with native service worker APIs.
 * Provides periodic update checks with a 10-minute cooldown and
 * visibility-based checks when the app returns to foreground.
 */
@Injectable({
  providedIn: 'root'
})
export class NativeSwUpdateService implements OnDestroy {
  private readonly UPDATE_CHECK_COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes
  private static readonly RELOAD_LOOP_KEY = 'native_sw_activate_reload_count';
  /** Session budget for activate+reload (AC: max 2 reloads). */
  private static readonly MAX_RELOAD_ATTEMPTS = 2;
  private lastUpdateCheck = 0;
  private subscriptions: Subscription[] = [];
  /** Stored so it can be removed in ngOnDestroy — prevents leaks and duplicate listeners on HMR/re-instantiation. */
  private controllerChangeHandler: (() => void) | null = null;
  /** In-memory one-time guard: ensures a single rapid burst of controllerchange events only triggers one reload. */
  private controllerChangeHandled = false;

  constructor() {
    this.initializeUpdateChecks();
  }

  private initializeUpdateChecks(): void {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers not supported');
      return;
    }

    // Reload the page whenever an *existing* controller is replaced by a new service worker,
    // so users immediately get the latest index.html (including inline stubs such as the
    // window.fbq stub that guards against GTM firing fbq() before fbevents.js loads).
    // Guards:
    //  - hadController flag: skips the reload on first-time SW registration (no existing
    //    controller), but updates to true so that a subsequent update in the same long-lived
    //    SPA session will still trigger a reload.
    //  - in-memory flag: prevents multiple rapid reloads from a burst of controllerchange events.
    //  - session reload budget (MAX_RELOAD_ATTEMPTS): prevents runaway reload loops across navigations.
    let hadController = !!navigator.serviceWorker.controller;
    this.controllerChangeHandler = () => {
      if (!hadController) {
        // No prior controller — first-time SW registration. Mark as having a controller now
        // so the next update in this session will trigger a reload.
        hadController = true;
        return;
      }
      if (this.controllerChangeHandled) {
        return;
      }
      if (this.getReloadAttemptCount() < NativeSwUpdateService.MAX_RELOAD_ATTEMPTS) {
        // Set the in-memory guard only when we are actually going to reload,
        // so an exhausted session budget does not permanently silence future
        // controllerchange events if the budget is reset elsewhere.
        this.controllerChangeHandled = true;
        this.incrementReloadAttemptCount();
        this.triggerReload();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', this.controllerChangeHandler);

    // Check for updates when app becomes visible
    const visibilityChangeSub = fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => this.checkForUpdate());

    // Periodic update checks every 10 minutes
    const intervalSub = interval(this.UPDATE_CHECK_COOLDOWN_MS)
      .subscribe(() => this.checkForUpdate());

    this.subscriptions.push(visibilityChangeSub, intervalSub);
  }

  /**
   * Check for service worker updates with cooldown
   */
  async checkForUpdate(): Promise<void> {
    const now = Date.now();

    if (now - this.lastUpdateCheck < this.UPDATE_CHECK_COOLDOWN_MS) {
      return;
    }

    this.lastUpdateCheck = now;

    try {
      const registration = await navigator.serviceWorker.getRegistration('/');
      await registration?.update();
    } catch (err) {
      console.warn('Service worker update check failed:', err);
    }
  }

  /**
   * Force activate waiting service worker.
   * Reload + session budget are owned by the permanent controllerchange listener
   * so this method only kicks SKIP_WAITING and does not double-count attempts.
   */
  async activateUpdate(): Promise<void> {
    try {
      // If the controllerchange listener has already scheduled a reload, the new SW
      // has already taken control — nothing left to do.
      if (this.controllerChangeHandled) {
        return;
      }

      // Only post SKIP_WAITING; the permanent controllerchange listener owns reload
      // and budget tracking. Keeping the budget check here would prevent SW activation
      // entirely when the budget is exhausted, leaving the app stuck on an old worker.
      const registration = await navigator.serviceWorker.getRegistration('/');

      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // The permanent controllerchange listener owns the reload + budget tracking.
        // activateUpdate() only needs to kick SKIP_WAITING; the listener will handle
        // the page refresh once the new SW takes control.
      }
    } catch (err) {
      console.error('Failed to activate service worker update:', err);
    }
  }

  resetReloadAttemptCount(): void {
    try {
      sessionStorage.removeItem(NativeSwUpdateService.RELOAD_LOOP_KEY);
    } catch {
      // sessionStorage may be unavailable
    }
  }

  private getReloadAttemptCount(): number {
    try {
      const raw = sessionStorage.getItem(NativeSwUpdateService.RELOAD_LOOP_KEY);
      const parsed = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    } catch {
      return 0;
    }
  }

  private incrementReloadAttemptCount(): void {
    try {
      const count = this.getReloadAttemptCount() + 1;
      sessionStorage.setItem(NativeSwUpdateService.RELOAD_LOOP_KEY, count.toString());
    } catch {
      // sessionStorage may be unavailable
    }
  }

  /**
   * Extracted for testability — tests spy on this instead of mocking window.location,
   * which is non-configurable in newer jsdom versions.
   * @internal
   */
  protected triggerReload(): void {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.controllerChangeHandler &&
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('controllerchange', this.controllerChangeHandler);
      this.controllerChangeHandler = null;
    }
  }
}
